import React from 'react';
import PropTypes from 'prop-types';

import stringUtils from '../../../../qti/utils/StringUtils';
import UtilService from '../../../../services/UtilService';
import MathWorkingInput from './MathWorkingInput';
import MathWorkingMenu from './MathWorkingMenu';
import MathWorkingRowsContainer from './MathWorkingRowsContainer';

import questionReader from '../../../../store/readHelpers/QuestionReader';
import settingsReader from '../../../../store/readHelpers/SettingsReader';

import styles from './mathWorking.scss';
import Prompt from '../Prompt';

export function getParagraphsContentFromString(str) {
    const regexp = new RegExp('<p>(.*?)</p>', 'gi');
    const lines = [];
    let matches;

    while ((matches = regexp.exec(str)) !== null) { // eslint-disable-line no-cond-assign
        lines.push(matches[1]);
    }
    return lines;
}

/**
 * Model for a math working row.
 * id: mathEntryInput.getMathEditableId(),
 * index: the index based order of the math row
 * mathText: mathRowDOM.innerHTML,
 * instance: MathEntryInput instance related to this model
 * autoFocus: true | false - whether to focus the row after its initialization.
 * @type {*}
 */
function MathWorkingRowModel(options = {}) {
    const defaultOptions = {
        id: undefined,
        mathText: '',
        instance: undefined,
        autoFocus: false,
    };
    Object.assign(this, defaultOptions, options);
}

class MathWorkingInteraction extends React.PureComponent {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        isReadOnly: PropTypes.bool,
        isCurrent: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        userResponse: PropTypes.array.isRequired,
        questionModel: PropTypes.object,
        direction: PropTypes.string,
    };

    constructor(props, context) {
        super(props, context);
        const mathRowsInstances = this.getMathRowsFromResponse(this.getUserResponse());

        this.state = {
            mathRowsInstances: mathRowsInstances.length > 0 ? mathRowsInstances : [new MathWorkingRowModel({
                index: 0,
                mathText: '',
                autoFocus: true,
            })],
        };
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.userResponse !== this.props.userResponse) {
            const mathRowsInstances = this.getMathRowsFromResponse(
                this.getUserResponse(nextProps.userResponse),
                this.state.mathRowsInstances,
            );

            this.setState({ mathRowsInstances });
        }
    }

    componentWillUnmount() {
        this.setState({
            mathRowsInstances: [],
        });
    }

    /**
     * Get first el from this.props.userResponse array
     * @returns {string}
     */
    getUserResponse = (nextPropsUserResponse) => {
        const userResponseArr = nextPropsUserResponse || this.props.userResponse;
        return Array.isArray(userResponseArr) && userResponseArr.length > 0 ? userResponseArr[0] : '';
    };

    getMathRowsFromResponse = (userResponse, existingMathRows = []) => {
        const { initialNumberOfLines = 1 } = this.props.model;
        let rowResponses;

        if (userResponse === '') {
            rowResponses = Array(initialNumberOfLines).fill('');
        } else {
            rowResponses = getParagraphsContentFromString(userResponse);
        }

        const mathRows = rowResponses.map((mathText, index) => {
            const mathRow = existingMathRows[index] || new MathWorkingRowModel();
            mathRow.mathText = mathText;
            return mathRow;
        });


        if (userResponse === '') {
            // auto-select the first math working as there is now user response yet
            mathRows[0].autoFocus = this.props.isCurrent;
        } else {
            // auto-select last math working row when question has been navigated to
            mathRows[mathRows.length - 1].autoFocus = this.props.isCurrent;
        }

        return mathRows;
    };

    handleUserResponse = () => {
        const { model } = this.props;
        this.props.callback(model.identifier, [this.getValue()]);
    };

    getValue = () => {
        const mathRows = this.state.mathRowsInstances;
        let retVal = '';

        mathRows.forEach((mathRowModel) => {
            const rowText = mathRowModel.instance ? mathRowModel.instance.getValue() : mathRowModel.mathText;
            retVal += `<p>${rowText}</p>`;
        });

        return (retVal === '') ? '' : `<div>${retVal}</div>`;
    };

    getMathRowIndex = (mathRowModel) => {
        return this.state.mathRowsInstances.findIndex((el) => {
            return (el.id === mathRowModel.id);
        });
    };

    onAddRow = (mathRowModel, duplicateRow) => {
        const newRowIndex = this.getMathRowIndex(mathRowModel) + 1;
        const newRow = new MathWorkingRowModel({
            autoFocus: true,
            mathText: duplicateRow ? mathRowModel.mathText : '',
        });

        const mathRowsInstances = UtilService.insertInArray(newRow, newRowIndex, [...this.state.mathRowsInstances]);
        this.hideRowSymbolsMenu(mathRowModel);

        this.setState(
            { mathRowsInstances },
            () => this.handleUserResponse()
        );
    };

    onDeleteRow = (mathRowModel) => {
        const removeIndex = this.getMathRowIndex(mathRowModel);
        let mathRowsInstances = [...this.state.mathRowsInstances];

        if (this.state.mathRowsInstances.length === 1) {
            // empty row contents
            mathRowModel.instance.mathquillUIElement.currentMathEditor.select();
            mathRowModel.instance.mathquillUIElement.currentMathEditor.write('');
            mathRowModel.mathText = '';
        } else {
            // remove row
            mathRowsInstances = UtilService.removeFromArray(removeIndex, mathRowsInstances);
            this.hideRowSymbolsMenu(mathRowModel);
        }

        this.setState({
            mathRowsInstances,
        }, () => {
            // set focus on previous available row
            const rowToFocus = this.state.mathRowsInstances[Math.max(0, removeIndex - 1)];
            rowToFocus.instance.mathquillUIElement.onMathEditorClicked();
            this.handleUserResponse();
        });
    };

    onDuplicateRow = (rowModel) => {
        this.onAddRow(rowModel, true);
    };

    hideRowSymbolsMenu = (mathRowModel) => {
        if (mathRowModel.instance) {
            mathRowModel.instance.mathquillUIElement.removeMenu();
        }
    };

    buildMathRows = () => {
        const mathRows = [];
        const { isReadOnly = false, questionModel } = this.props;
        let mathSymbolPalettePreset;

        if (settingsReader.isMathSymbolPalettePresetEnabled()) {
            mathSymbolPalettePreset = questionReader.getMathSymbolPalettePreset(questionModel);
        }

        this.state.mathRowsInstances.forEach((mathRowModel) => {
            let mathWorkingMenu;

            if (mathRowModel.id === undefined) {
                mathRowModel.id = stringUtils.getUID();
            }

            if (!isReadOnly) {
                mathWorkingMenu = (
                    <MathWorkingMenu
                        key={`MathWorkingMenu_${mathRowModel.id}`}
                        mathRowModel={mathRowModel}
                        onAddRow={this.onAddRow}
                        onDuplicateRow={this.onDuplicateRow}
                        onDeleteRow={this.onDeleteRow}
                    />
                );
            }

            mathRows.push(
                <MathWorkingInput
                    {...this.props}
                    extraChildren={mathWorkingMenu}
                    isReadOnly={isReadOnly}
                    key={`MathEntryInput_${mathRowModel.id}`}
                    mathRowModel={mathRowModel}
                    parent={this}
                    mathSymbolPalettePreset={mathSymbolPalettePreset}
                />
            );
        });

        return mathRows;
    };

    render() {
        const { model, direction } = this.props;
        const mathRows = this.buildMathRows();
        const className = `${styles.mathWorkingInteraction} interaction`;
        const inlineStyle = {};

        if (this.props.model.fieldWidth) {
            inlineStyle.width = this.props.model.fieldWidth;
        }

        return (
            <div className={className}>
                <Prompt model={model.prompt} />
                <div className="extended-text-math" style={inlineStyle} id={`${model.identifier}_valueId`} dir={direction}>
                    <MathWorkingRowsContainer mathRows={mathRows} />
                </div>
            </div>
        );
    }
}

export default MathWorkingInteraction;
